import { X, Search, Clock } from 'react-feather';
import Text from 'atoms/Text';
import Tooltip from 'organisms/Tooltip';
import { useState, useMemo, useEffect, useRef } from 'react';
import useChildWindows from '../hooks/useChildWindows';

// Popup `command=` query value (or path) → display command for line 1.
const COMMAND_ALIASES = {
    plot: 'PLOT',
    plota: 'PLOTA',
    plotf: 'PLOTF',
    curve: 'CURVE',
    basis: 'BASIS',
    regress: 'REGRESS',
    regr: 'REGRESS',
    anchor: 'ANCHOR',
    thor: 'THOR',
    arc: 'ARC',
    stats: 'STATS',
    edge: 'EDGE',
    fwcm: 'FWCM',
    trace: 'TRACE',
    chronos: 'CHRONOS',
    ctascan: 'CTASCAN',
    omon: 'OMON',
    dslice: 'DSLICE',
    atlas: 'ATLAS'
};

export const PATH_RULES = [
    [/\/plotf\//i, 'PLOTF'],
    [/\/plot\//i, 'PLOT'],
    [/\/curve\//i, 'CURVE'],
    [/\/arc\//i, 'ARC'],
    [/\/edge\//i, 'EDGE'],
    [/\/fwcm\//i, 'FWCM'],
    [/\/chronos\b/i, 'CHRONOS'],
    [/\/stats\b/i, 'STATS'],
    [/\/atlas\b/i, 'ATLAS'],
    [/\/cta\b/i, 'CTASCAN'],
    [/\/omon\b/i, 'OMON'],
    [/\/dslice\b/i, 'DSLICE'],
    [/\/thor?\b/i, 'THOR'],
    [/\/regress/i, 'REGRESS'],
    [/\/(fx-options|vol)\b/i, 'FXO'],
    [/\/equities\//i, 'EQUITY'],
    [/\/indexes\//i, 'INDEX'],
    [/\/irs\b/i, 'IRS'],
    [/\/currencies\b/i, 'CCY'],
    [/\/cds\b/i, 'CDS'],
    [/\/bonds?\b/i, 'BOND'],
    [/\/matchings?\b/i, 'MATCH'],
    [/\/trades?\b/i, 'TRADE'],
    [/\/watchlist\b/i, 'WATCHLIST'],
    [/\/quick-price/i, 'QUICK PRICE'],
    [/\/hoot\b/i, 'HOOT'],
    [/\/chat\b/i, 'CHAT']
];

const parseUrl = (win) => {
    try {
        return new URL(win.url);
    } catch (e) {
        return null;
    }
};

const cleanTitle = (title) => (title ? title.replace(' | Deon', '').trim() : '');

const commandFromUrl = (win) => {
    const u = parseUrl(win);
    if (!u) return '';
    const cmd = (u.searchParams.get('command') || '').split('-')[0].toLowerCase();
    if (COMMAND_ALIASES[cmd]) return COMMAND_ALIASES[cmd];
    const rule = PATH_RULES.find(([re]) => re.test(u.pathname));
    return rule ? rule[1] : '';
};

// Line 1: prefer what the popup reported, else derive from the URL/title.
const commandFor = (win) => win.command
    || commandFromUrl(win)
    || cleanTitle(win.title)
    || win.name
    || 'WINDOW';

const lineTwoFromUrl = (win) => {
    const u = parseUrl(win);
    if (!u) return '';
    const legs = u.searchParams.get('legs');
    if (legs) {
        return legs
            .split(',')
            .map((l) => l.trim())
            .filter(Boolean)
            .join(', ');
    }
    return u.searchParams.get('ticker') || '';
};

const subtitleFor = (win) => {
    if (win.line2) return win.line2;
    const t = cleanTitle(win.title);
    if (t && t !== 'Deon') return t;
    const fromUrl = lineTwoFromUrl(win);
    if (fromUrl) return fromUrl;
    const u = parseUrl(win);
    return u ? u.pathname : win.url || win.name || '';
};

const timeFor = (openedAt) => {
    if (!openedAt) return '';
    try { return new Date(openedAt).toLocaleTimeString(); } catch (e) { return ''; }
};

// Renders the card subtitle, showing the styled Tooltip only when the text is
// actually clipped by `truncate` (scrollWidth > clientWidth).
const TruncatedSubtitle = ({ text }) => {
    const ref = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
        const el = ref.current?.querySelector('p');
        if (!el) return undefined;

        const measure = () => setIsTruncated(el.scrollWidth > el.clientWidth);
        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [text]);

    return (
        <Tooltip
            content={text}
            position="top"
            parentClass="block w-full min-w-0"
            disabled={!isTruncated}
        >
            <span ref={ref} className="block w-full min-w-0">
                <Text
                    variant="caption"
                    className="text-gray-200 my-2 truncate block w-full min-w-0"
                    fontWeight="font-semibold"
                >
                    {text}
                </Text>
            </span>
        </Tooltip>
    );
};

const CommandCard = ({
    item,
    focusWindow,
    closeWindow,
    reopenWindow
}) => (
    <div className="bg-gray-800 rounded px-2.5 py-2 border-l-2 border-transparent hover:bg-success-500/15 hover:border-success-500">
        <div className="flex justify-between items-center gap-3">
            <div>
                <Text
                    variant="bodySmall"
                    fontWeight="font-semibold"
                    className="text-gray-100"
                >
                    {commandFor(item)}
                </Text>
            </div>

            <div className="flex gap-2">
                {item?.closed ? (
                    <>
                        <button
                            onClick={() => reopenWindow(item.id)}
                            className="
                                h-6 px-3
                                rounded
                                border border-gray-500 hover:border-success-500
                                text-success-500
                                text-xs
                                font-semibold
                                cursor-pointer
                            "
                        >
                            Open
                        </button>

                        <button
                            className="
                                h-6 px-3
                                rounded
                                border border-gray-500
                                text-gray-200
                                text-xs
                                font-semibold
                                opacity-50 cursor-not-allowed pointer-events-none
                            "
                        >
                            Close
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            onClick={() => focusWindow(item.id)}
                            className="
                                h-6 px-3
                                rounded
                                border border-gray-500 hover:border-success-500
                                text-success-500
                                text-xs
                                font-semibold
                                cursor-pointer
                            "
                        >
                            Focus
                        </button>

                        <button
                            onClick={() => closeWindow(item.id)}
                            className="
                                h-6 px-3
                                rounded
                                border border-gray-500 hover:border-success-500
                                text-gray-200
                                text-xs
                                font-semibold
                                cursor-pointer
                            "
                        >
                            Close
                        </button>
                    </>
                )}
            </div>
        </div>

        <TruncatedSubtitle text={subtitleFor(item)} />

        <div className="flex items-center justify-between">
            <div
                className={`
                    flex items-center gap-1
                    px-2 py-0.5
                    rounded
                    ${!item?.closed ? 'bg-success-300/10 border-success-300/20' : 'bg-error-300/10 border-error-300/20'}
                    border
                    w-max
                `}
            >
                <div className={`w-1.5 h-1.5 rounded-full ${!item?.closed ? 'bg-success-300' : 'bg-error-300'} animate-flash`} />

                <Text
                    variant="small"
                    className={`${!item?.closed ? 'text-success-300' : 'text-error-300'}`}
                    fontWeight="font-semibold"
                >
                    {!item?.closed ? 'Open' : 'Closed'}
                </Text>
            </div>

            <div className="flex items-center gap-1">
                <Clock size={12} className="text-gray-200" />

                <Text
                    variant="small"
                    className="text-gray-200"
                >
                    {!item?.closed ? `Opened ${timeFor(item.openedAt)}` : `Closed ${timeFor(item.closedAt)}`}
                </Text>
            </div>
        </div>
    </div >
);

const ChildWindowsSidebar = ({
    isOpen = true,
    onClose
}) => {
    const { windows, count, closeAll, closeWindow, focusWindow, focusAll, clearClosed, reopenWindow } = useChildWindows();
    const [filter, setFilter] = useState('');
    const prevOpen = useRef(isOpen);
    useEffect(() => {
        if (prevOpen.current && !isOpen) clearClosed();
        prevOpen.current = isOpen;
    }, [isOpen, clearClosed]);
    const commands = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return windows;
        return windows.filter((w) => commandFor(w).toLowerCase().includes(q) || subtitleFor(w).toLowerCase().includes(q));
    }, [windows, filter]);

    return (
        <div
            className={`
                    fixed top-0 right-0 h-screen
                    w-[300px]
                    bg-gray-900
                    border-l border-gray-600
                    transition-transform duration-300 ease-in-out
                    z-50
                    ${isOpen ? '-translate-x-12' : 'translate-x-full'}
                `}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-600">
                <div className="flex items-center gap-3">
                    <Text
                        variant="bodySmall"
                        fontWeight="font-semibold"
                        className="text-gray-100"
                    >
                        Child Windows
                    </Text>

                    <div className="px-2 py-1 rounded-md border border-success-300">
                        <Text
                            variant="caption"
                            className="text-success-300"
                            fontWeight="font-semibold"
                        >
                            Open {count}
                        </Text>
                    </div>
                </div>

                <X
                    size={16}
                    className="text-gray-200 hover:text-gray-100 cursor-pointer"
                    onClick={() => {
                        setFilter('');
                        onClose();
                    }
                    }
                />
            </div>

            {/* Search */}
            <div className="p-3 border-b border-gray-700">
                <div className="h-8 flex items-center gap-1 px-3 rounded-md border border-gray-600">
                    <Search size={16} className="text-gray-200" />
                    <input
                        placeholder="Filter open windows..."
                        className="bg-transparent outline-none text-gray-100 text-xs placeholder:text-gray-200"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />
                </div>

                <div className="flex gap-2 mt-2">
                    <button
                        onClick={focusAll}
                        className="
                                flex-1 h-6
                                rounded border border-gray-500
                                text-success-500 text-xs font-semibold hover:border-success-500
                            "
                    >
                        Focus All
                    </button>

                    <button
                        onClick={closeAll}
                        className="
                                flex-1 h-6
                                rounded border border-gray-500
                                text-gray-200 text-xs font-semibold hover:border-success-500
                            "
                    >
                        Close All
                    </button>
                </div>
            </div>

            {/* List */}
            <div className="overflow-y-auto h-[calc(100vh-140px)] p-3 space-y-2">
                {commands.length === 0
                    ? <div className='text-center mt-16'>
                        <img src="/images/icons/bird.svg" alt="No Active Commands" className="mx-auto mb-2" />
                        <Text variant="bodySmall" className="text-gray-200">No Active Commands</Text>
                        <Text variant="bodySmall" className="text-gray-200">All command windows have been closed.</Text>
                    </div>
                    : commands.map((item) => (
                        <CommandCard
                            key={item.id}
                            item={item}
                            focusWindow={focusWindow}
                            closeWindow={closeWindow}
                            reopenWindow={reopenWindow}
                        />
                    ))}
            </div>
        </div>
    );
};

export default ChildWindowsSidebar;
